import type { AnySection, PresetSection, Structure, SuperApp, ThemedSection } from '@wix/add-panel-common';
import type { ComponentType, CSSProperties, MouseEvent } from 'react';
import type { ProcessPlaceholders } from '../utils/processPlaceholders';

export interface FontMap {
  bold: boolean;
  color: string;
  cssColor: string;
  family: string;
  fontWithFallbacks: string;
  italic: boolean;
  lineHeight: CSSProperties['lineHeight'];
  size: string;
  style: string;
  variant: string;
  weight: CSSProperties['fontWeight'];
}

export type FontsMap = Record<string, FontMap>;

export interface TextTheme {
  fontStyle: 'normal' | 'italic';
  fontVariant: 'normal' | 'small-caps' | 'inherit';
  fontWeight: 'normal' | 'bold' | 'bolder' | 'lighter';
  color: string;
  fontFamily: string;
  fontSize: string;
  lineHeight: string;
  letterSpacing: string;
}

export type TextThemes = Record<string, TextTheme>;

export interface ClickHandlerCompData {
  rect: { left: number; top: number; width: number; height: number };
  structure: Structure;
  sectionTitle: string;
  categoryId: string;
}

export enum SectionViewParentType {
  AddPanel = 'ADD_PANEL',
  DesignPanel = 'DESIGN_PANEL',
}

export interface ThemedPreviewProps {
  title: string;
  categoryId: string;
  parentType: SectionViewParentType;
  showSectionHeader: false;
  onClick: (structure: Structure, sectionName: string | undefined, tags: string | undefined, itemId: string) => void;
  onMouseEnter?: (compDef: Structure, itemId: string) => void;
  onMouseLeave?: (itemId: string) => void;
  onItemDrag: (event: MouseEvent, compData: ClickHandlerCompData) => void;
  props: ThemedSection['props'];
  hide?: boolean;
  automationId?: string;
  className?: string;
  style?: CSSProperties;
  selectedItemKey?: string;
  isSelectedItem?: (itemKey: string) => boolean;
}

export interface MobileToolsProps {
  openLeftbarSidePanel?: (sideBarComponent: React.ReactNode) => void;
}

export interface ConditionHelpers {
  isSectionAvailable: (section: AnySection) => boolean;
  isRepeaterItem: () => boolean;
  isMembersAppInstalled: () => boolean;
  isTPAInstalled: (tpaId: string) => boolean;
  isSuperAppInstalled: (superAppId: string) => boolean;
  isDataModeOn: () => boolean;
  isDevModeOn: () => boolean;
  isAdminPage: () => boolean;
  isProvisionedOn: () => boolean;
  isMultilingualModeOn: () => boolean;
  isMobileEditorOn: () => boolean;
  isEducationModeOn: () => boolean;
  isInteractionModeOn: () => boolean;
  isLightboxModeOn: () => boolean;
  isFullWidthLightboxModeOn: () => boolean;
  hasSavedComponents: () => boolean;
  hasMyWidgets: () => boolean;
  isRefComponentSupported: () => boolean;
  isStaticPagesOverLimit: () => boolean;
  isConnectablePage: () => boolean;
  isCustomMenuEnabled: () => boolean;
  hasCustomTheme: (compType: string) => boolean;
}

export interface IncomingUtils {
  getBlankSectionStructure: (params: { height: number; y: number }) => Structure;
  shouldUseTextThemes: () => boolean;
  shouldShowMyDesignsFooter: () => boolean;
  media: {
    getMediaUrl: (mediaPath: string, fallback?: string) => string;
    getVideoIfAvailable: (sectionKey: string, videoIdKey: string) => false | string;
  };
  translate: (key: string, values?: Record<string, string>) => string;
  serviceTopology: {
    editorAssetsBaseUrl: string;
    itemsDataBaseUrl: string;
  };
  theme: {
    fonts: {
      getMap: () => FontsMap;
    };
    textThemes: {
      getAll: () => TextThemes;
    };
  };
  getRegistrySections: () => Map<string, PresetSection>;
  hasSectionStyle: (section: ThemedSection) => boolean;
  ThemedPreview: ComponentType<ThemedPreviewProps>;
  PersonalizedPreview: ComponentType<ThemedPreviewProps>;
  MobileToolsPanel: ComponentType<MobileToolsProps>;
  uiAnimations: {
    scrollTo: (
      scrollPosition: Partial<{ x: number; y: number }>,
      durationInSeconds: number,
      delay: number,
      root: HTMLElement,
      onComplete?: () => void,
    ) => void;
  };
  getSuperAppData: (appDefinitionId: string) => Promise<SuperApp>;
  conditionHelpers: ConditionHelpers;
  getLiveChatSectionColors: () => { barColor: string; bubbleColor: string };
}

export interface Utils extends IncomingUtils {
  processPlaceholders: ProcessPlaceholders;
}

export interface UtilsProviderProps {
  utils: IncomingUtils;
}
